import { useMissionListingStore } from '../store/useMissionListingStore';
import {
  FETCH_MISSIONS,
  FetchMissionsBySchoolAndSchoolYearResponse,
  MissionsListQuery,
  MissionsListQueryVariables,
} from '@edwin-group/cj-graphql';
import { useDeferredValue, useMemo } from 'react';
import { GetCurrentSchoolYearUseCase } from '../../use-cases/get-current-school-year';
import { useAuthentication } from '@/hooks/useAuthentication';
import { useSuspenseQuery } from '@apollo/client';

export const useList = () => {
  const resultsSection = useMissionListingStore((state) => state.missionListPage?.resultsSection);
  const filtersSelected = useMissionListingStore((state) => state.filters);
  const updateTotalCountWithoutFilters = useMissionListingStore((state) => state.updateTotalCountWithoutFilters);
  const totalCountWithoutFilters = useMissionListingStore((state) => state.totalCountWithoutFilters);

  const { schoolContext } = useAuthentication();

  const queryVariablesMemo = useMemo(() => {
    return {
      documentIds: filtersSelected.missionsDocumentIds || [],
      roleModel: filtersSelected.searchTerm,
      keyStages: filtersSelected.keyStages,
      traits: filtersSelected.characterTraits,
      locations: filtersSelected.locations,
      pagination: { start: 0, limit: 6 },
      schoolYear: GetCurrentSchoolYearUseCase.execute(),
      schoolDocumentId: schoolContext?.documentId || '',
    };
  }, [filtersSelected, schoolContext?.documentId]);

  const queryVariables = useDeferredValue(queryVariablesMemo);
  const isPending = queryVariablesMemo !== queryVariables;

  const {
    data: missionsListResult,
    fetchMore,
    error: missionsListError,
  } = useSuspenseQuery<MissionsListQuery, MissionsListQueryVariables>(FETCH_MISSIONS, {
    variables: queryVariables,
    fetchPolicy: 'cache-and-network',
  });

  const missionUserCount = missionsListResult?.fetchMissionsBySchoolAndSchoolYear?.pageInfo?.total || 0;

  if (totalCountWithoutFilters == null) {
    updateTotalCountWithoutFilters(missionUserCount);
  }

  const onFetchMore = useMemo(
    () => () => {
      fetchMore({
        variables: {
          ...queryVariables,
          pagination: {
            start: missionsListResult?.fetchMissionsBySchoolAndSchoolYear?.nodes?.length || 0,
            limit: 4,
          },
        },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult) return prev;

          const newMissions = fetchMoreResult?.fetchMissionsBySchoolAndSchoolYear?.nodes?.filter(
            (newItem) =>
              !prev?.fetchMissionsBySchoolAndSchoolYear?.nodes?.some((prevItem) => prevItem.slug === newItem.slug),
          );

          return {
            ...prev,
            fetchMissionsBySchoolAndSchoolYear: {
              ...prev.fetchMissionsBySchoolAndSchoolYear,
              nodes: [...(prev?.fetchMissionsBySchoolAndSchoolYear?.nodes || []), ...(newMissions || [])],
            },
          };
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [missionsListResult?.fetchMissionsBySchoolAndSchoolYear?.nodes?.length, queryVariables],
  );

  const missionsList =
    missionsListResult?.fetchMissionsBySchoolAndSchoolYear as FetchMissionsBySchoolAndSchoolYearResponse;

  return {
    totalCountWithoutFilters,
    missionUserCount,
    resultsSection,
    missionsList,
    onFetchMore,
    isPending,
    hasMore:
      (missionsListResult?.fetchMissionsBySchoolAndSchoolYear?.pageInfo?.total || 0) >
      (missionsList?.nodes?.length || 0),
    errors: [missionsListError].filter(Boolean),
  };
};
